/* global React */
/* ──────────────────────────────────────────────────────────────────────
   ContactPage — /contact/
   Letterhead direction (A) with C-flavoured animation:
     ▸ Per-char reveal of "Hi —" with a deliberate pause before the dash
     ▸ Aqua glow pulse on the greeting (settles into a soft permanent aura)
     ▸ Blinking caret cursor after the dash (mid-write feel)
     ▸ Drifting aqua-tinted background (28s loop, very subtle)

   Left column: the site-wide inquiry form (window.DzInquiryForm from
   js/CtaStrip.jsx). Reach panel (right col) uses an icon-led layout.
   Office data is NOT rendered here — the global footer's offices column
   (hover popovers) covers that.
   ────────────────────────────────────────────────────────────────────── */

/* ─── Reach-panel icons (inline SVG, scoped to this file) ─── */
function ContactReachIcon({ name }) {
  if (name === 'email') {
    return (
      <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2.25" y="3.75" width="13.5" height="10.5" rx="2" />
        <path d="M3 4.75L9 9.75L15 4.75" />
      </svg>
    );
  }
  if (name === 'phone') {
    return (
      <svg viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
        <path d="M3.43 1.96a1.7 1.7 0 0 1 2.07-1.19l1.78.48a1.7 1.7 0 0 1 1.21 2.05l-.31 1.18a1.7 1.7 0 0 1-1.21 1.21l-.86.23a8.9 8.9 0 0 0 3.66 3.66l.23-.86a1.7 1.7 0 0 1 1.21-1.21l1.18-.31a1.7 1.7 0 0 1 2.05 1.21l.48 1.78a1.7 1.7 0 0 1-1.19 2.07A12.85 12.85 0 0 1 3.43 1.96z" transform="translate(0 0.5)"/>
      </svg>
    );
  }
  if (name === 'whatsapp') {
    return (
      <svg viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
        <path d="M9 1.5a7.5 7.5 0 0 0-7.5 7.5 7.42 7.42 0 0 0 1.08 3.86L1.5 16.5l3.74-1.04A7.5 7.5 0 1 0 9 1.5zm4.32 10.58c-.18.5-1.06.97-1.47 1.02-.39.06-.87.08-1.4-.09a13.1 13.1 0 0 1-1.27-.47 10.04 10.04 0 0 1-3.85-3.4 4.42 4.42 0 0 1-.92-2.34c0-.56.2-1.13.55-1.49a.63.63 0 0 1 .44-.2h.32c.1 0 .24-.04.36.28l.51 1.23c.04.1.07.21.01.34l-.15.22c-.07.1-.15.22-.21.3-.07.08-.15.17-.06.33.08.15.38.62.82 1 .57.5 1.04.66 1.18.74.15.07.24.06.33-.04.08-.08.38-.45.49-.6.1-.15.2-.13.35-.08.15.05.93.44 1.13.53.13.06.23.1.26.16.04.07.04.39-.15.89z"/>
      </svg>
    );
  }
  return null;
}

/* ─── Page ─── */
function ContactPage() {
  const data = (typeof window !== 'undefined' && window.DZ_CONTACT) || {};
  if (!data.hero || !data.reach) {
    return null;
  }

  // ── Greeting tokens (locale-aware) ──
  // English reveals per character. Arabic must NOT be split per character:
  // each <span class="ch"> is display:inline-block, which forces isolated
  // glyph forms and breaks Arabic letter joining. For Arabic we merge the
  // character tokens into whole-word spans (a whole word in one span shapes
  // correctly) while keeping the trailing dash as its own delayed reveal.
  // English behaviour is left exactly as before.
  const greetText = (data.hero && data.hero.greet) || 'Hi —';
  const greetRaw = (data.hero && data.hero.greetChars) ||
    [{ text: 'H', dash: false }, { text: 'i', dash: false }, { text: ' —', dash: true }];
  const greetTokens = /[\u0600-\u06FF]/.test(greetText)
    ? greetRaw.reduce((acc, c) => {
        const prev = acc[acc.length - 1];
        if (!c.dash && prev && !prev.dash) prev.text += c.text;
        else acc.push({ text: c.text, dash: !!c.dash });
        return acc;
      }, [])
    : greetRaw;

  return (
    <main className="dz-contact" id="dz-main" aria-labelledby="contact-title">
      <div className="dz-contact__inner">

        {/* ── Greeting (per-char reveal for English; word-level for Arabic to preserve shaping) ── */}
        <h1 className="dz-contact__greet" id="contact-title" aria-label={greetText}>
          {greetTokens.map((c, i) => (
            <span key={i} className={'ch' + (c.dash ? ' ch-dash' : '')}>{c.text}</span>
          ))}
        </h1>

        {/* ── Intro line (with inline <em> via dangerouslySetInnerHTML) ── */}
        <p
          className="dz-contact__intro"
          dangerouslySetInnerHTML={{ __html: data.hero.intro }}
        />

        {/* ── Compose split: form (left, ~58%) + reach panel (right, ~42%) ── */}
        <section className="dz-contact__compose" aria-label="Contact form and direct contact methods">

          {/* The site-wide inquiry form (js/CtaStrip.jsx) — same fields and sending as every page. */}
          {window.DzInquiryForm ? <window.DzInquiryForm idPrefix="cf" /> : null}

          {/* Reach panel — icon-led horizontal rows (visually distinct from the form) */}
          <aside className="dz-contact__reach" aria-label="Direct contact methods">
            <p className="dz-contact__reach-heading">{data.reach.heading}</p>
            <ul className="dz-contact__reach-list">
              {data.reach.items.map((item) => (
                <li key={item.label} className="dz-contact__reach-item">
                  <a
                    href={item.href}
                    className="dz-contact__reach-row"
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                  >
                    <span className="dz-contact__reach-ico" aria-hidden="true">
                      <ContactReachIcon name={item.icon} />
                    </span>
                    <span className="dz-contact__reach-body">
                      <span className="dz-contact__reach-label">{item.label}</span>
                      <span className="dz-contact__reach-value">{item.value}</span>
                    </span>
                    <span className="dz-contact__reach-arrow" aria-hidden="true">→</span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="dz-contact__reach-note">{data.reach.note}</p>
          </aside>

        </section>

      </div>
    </main>
  );
}

window.ContactPage = ContactPage;
